import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PortfolioUrls } from 'config/urls';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';
import { formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any;
    variables: any;
    queryConfig?: {
        enabled?: boolean;
    };
    attributeName: string;
    offset: number;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
}

function useFetchROIMediaTypeData({ queryKey, variables, queryConfig = {}, attributeName, offset }: Props): outputType {
    const { appConfig } = useContext(UserContext);

    const { mediaSpendsLabelKeyMapping } = getConfig(ConfigKeys.PORTFOLIO_ANALYSIS, appConfig);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState<number>(0);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            if (!offset) setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: PortfolioUrls.MediaTypesDataUrl,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData: any = parseTableData(mediaSpendsLabelKeyMapping, payload?.data, attributeName);
            if (offset) {
                setData([...data, ...parsedData]);
            } else {
                setData(parsedData);
                setCount(payload.total_count);
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchROIMediaTypeData };

const parseTableData = (mediaSpendsLabelKeyMapping: any, data: any, attributeName: string) => {
    const results: any[] = [];

    data.forEach((k: any) => {
        const temp: any = {
            [attributeName]: {
                data: k.attribute_name,
                unFormattedData: k.attribute_name,
            },
        };

        k.attribute_data.forEach((mt: any, index: number) => {
            temp[attributeName].rowSpan = index === 0 ? k.attribute_data?.length : 0;

            const rowData = JSON.parse(JSON.stringify(temp));
            Object.keys(mt).forEach((key_string) => {
                let data: any;
                let unFormattedData: any; //for export - show full numbers in export
                if (key_string === 'media_type') {
                    data = mt[key_string];
                    unFormattedData = data;
                } else if (key_string === 'share_of_media_spend') {
                    data = `${_.round(Number(mt[key_string])).toString()}%`;
                    unFormattedData = data;
                } else {
                    data = formatNumWithKMBAlt(mt[key_string], 2, 2);
                    unFormattedData = mt[key_string];
                }

                rowData[mediaSpendsLabelKeyMapping[key_string]] = {
                    data: data,
                    unFormattedData,
                    color: getColorCode(
                        key_string,
                        key_string === 'media_type' ? mt[key_string] : Number(mt[key_string]),
                    ),
                };
            });
            rowData['Media Type'].rowSpan = 1;
            results.push(rowData);
        });
    });
    return results;
};

const getColorCode = (key_string: string, value: any) => {
    const thresholdPointsForKPIs: any = {
        media_lift: 1,
        media_roi: 0,
        incremental_retail_sales: 0,
        incremental_profit: 0,
    };

    if (['media_lift', 'media_roi', 'incremental_retail_sales', 'incremental_profit'].includes(key_string)) {
        return value < thresholdPointsForKPIs[key_string]
            ? colorConstants.lightRed
            : value > thresholdPointsForKPIs[key_string]
            ? colorConstants.lightGreen
            : colorConstants.lightGrey;
    }
    return undefined;
};
